import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Fab, Stack, Typography } from '@mui/material';
import { ContentCopy, Sensors, Speed } from '@mui/icons-material';
import { Radio, ThreeDots } from 'react-loader-spinner';
import { CONNECTION_STATE_CHANGE } from '@aws-amplify/pubsub';
import { Hub } from 'aws-amplify/utils';
import ModalWithActions from '../../ModalWithActions';
import { connectionColorObj, primaryColorObj } from '../../../themes';
import { attachPubsubPolicy, pubsub } from '../../../services/pubsubService';
import CustomTooltip from '../../shared/atoms/CustomTooltip';
import { useGeneralContext } from '../../../context/GeneralContext';
import CustomVideoPlayer from '../../shared/atoms/CustomVideoPlayer';

const TestConnectionModal = ({
  modalShow,
  onSenderModalHide,
  sensor,
  parameter,
  measuringMode,
  credentials,
}) => {
  const { t } = useTranslation();
  const { openToast } = useGeneralContext();
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('Connecting');
  const sub = useRef(null);

  const subscribe = useCallback(
    timeout => {
      if (!sensor) return;
      if (sub.current) sub.current?.unsubscribe();
      setTimeout(() => {
        try {
          sub.current = pubsub
            .subscribe({ topics: process.env.REACT_APP_MQTT_TOPIC })
            .subscribe({
              next: data => {
                if (Array.isArray(data)) {
                  const packet = data.find(d => d.sensor_id === sensor);
                  if (packet) setMessage(JSON.stringify(packet, null, 2));
                } else if (data?.sensor_id === sensor) {
                  setMessage(JSON.stringify(data, null, 2));
                }
              },
              error: error => console.error(error),
              complete: () => console.warn('Done'),
            });
        } catch (error) {
          console.error(error);
          setStatus('Error');
        }
      }, timeout);
    },
    [sensor],
  );

  useEffect(() => {
    if (measuringMode === 'streaming') return;

    const hubListenerCancelToken = Hub.listen('pubsub', data => {
      const { payload } = data;
      if (payload.event === CONNECTION_STATE_CHANGE) {
        setStatus(payload.data.connectionState);
      }
    });
    attachPubsubPolicy()
      .then(() => {
        subscribe(0);
      })
      .catch(error => {
        console.error(error);
        setStatus('Error');
      });

    return () => {
      sub.current?.unsubscribe();
      hubListenerCancelToken?.();
    };
  }, [subscribe, measuringMode]);

  const payload = useMemo(() => {
    let parameters = {
      [parameter || 'reading']: measuringMode === 'enum' ? 'string' : 0,
    };
    if (measuringMode === 'positioning')
      parameters = {
        lng: -98.5556199,
        lat: 39.8097343,
      };
    return JSON.stringify(
      {
        sensor_id: sensor,
        timestamp: Date.now(),
        parameters,
      },
      null,
      2,
    );
  }, [measuringMode, parameter, sensor]);

  return (
    <ModalWithActions
      show={modalShow}
      modalTitle={t(
        'management_menu.asset_wizard.sensor.test_connection_modal.title',
      )}
      btActionText={t(
        'management_menu.asset_wizard.sensor.test_connection_modal.retry',
      )}
      actionButtonVisible={measuringMode !== 'streaming'}
      btCancelText={t('modal.close')}
      onHide={onSenderModalHide}
      action={() => subscribe(1000)}
      width="780px"
      inputComponent={
        measuringMode === 'streaming' ? (
          <Stack padding="0 40px 0 40px" justifyContent="center" spacing={2}>
            <Stack alignItems="center" minWidth="120px">
              <Radio
                height={60}
                width={60}
                colors={[
                  connectionColorObj[status],
                  connectionColorObj[status],
                  connectionColorObj[status],
                ]}
                wrapperStyle={{ cursor: 'default' }}
              />
              <Typography variant="body2">
                {t(
                  'management_menu.asset_wizard.sensor.test_connection_modal.connecting_status',
                )}
              </Typography>
              <Typography fontWeight="bold">
                {t(
                  `management_menu.asset_wizard.sensor.test_connection_modal.status`,
                  { status },
                )}
              </Typography>
              <Typography fontWeight="bold">{message}</Typography>
            </Stack>
            <CustomVideoPlayer
              id={sensor}
              credentials={credentials}
              setStatus={setStatus}
              setMessage={setMessage}
              height="374px"
            />
          </Stack>
        ) : (
          <Stack
            direction="row"
            padding="20px 40px 0 40px"
            justifyContent="center"
          >
            <Stack alignItems="center" minWidth="120px">
              <Sensors sx={{ fontSize: 60, color: primaryColorObj.default }} />
              <Typography variant="body2">
                {t(
                  'management_menu.asset_wizard.sensor.test_connection_modal.sensor_id',
                )}
              </Typography>
              <Typography fontWeight="bold" gutterBottom>
                {sensor}
              </Typography>
              <Box
                position="relative"
                sx={{
                  ':hover .MuiFab-root': {
                    display: 'block',
                  },
                }}
              >
                <pre
                  style={{
                    color: 'inherit',
                    textAlign: 'left',
                    margin: 0,
                    border: '1px solid white',
                    borderRadius: '8px',
                    padding: '5px',
                    backgroundColor: 'black',
                    cursor: 'text',
                    width: '300px',
                  }}
                >
                  {payload}
                </pre>
                <CustomTooltip
                  title={t(
                    'management_menu.asset_wizard.sensor.test_connection_modal.copy',
                  )}
                  placement="right"
                  leaveTouchDelay={0}
                >
                  <Fab
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      display: 'none',
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(payload);
                      openToast(
                        t(
                          'management_menu.asset_wizard.sensor.test_connection_modal.payload_copied',
                        ),
                      );
                    }}
                  >
                    <ContentCopy sx={{ color: 'white' }} />
                  </Fab>
                </CustomTooltip>
              </Box>
            </Stack>
            <ThreeDots
              color="white"
              height={50}
              width={50}
              wrapperStyle={{ margin: '10px', cursor: 'default' }}
            />
            <Stack alignItems="center" minWidth="120px">
              <Radio
                height={60}
                width={60}
                colors={[
                  connectionColorObj[status],
                  connectionColorObj[status],
                  connectionColorObj[status],
                ]}
                wrapperStyle={{ cursor: 'default' }}
              />
              <Typography variant="body2">
                {t(
                  'management_menu.asset_wizard.sensor.test_connection_modal.connecting_status',
                )}
              </Typography>
              <Typography fontWeight="bold">
                {t(
                  `management_menu.asset_wizard.sensor.test_connection_modal.status`,
                  { status },
                )}
              </Typography>
            </Stack>
            <ThreeDots
              color="white"
              height={50}
              width={50}
              wrapperStyle={{ margin: '10px', cursor: 'default' }}
            />
            <Stack alignItems="center" minWidth="220px">
              <Speed sx={{ fontSize: 60 }} />
              <Typography variant="body2" gutterBottom>
                {t(
                  'management_menu.asset_wizard.sensor.test_connection_modal.physical_device_reading',
                )}
              </Typography>
              {message ? (
                <pre
                  style={{
                    color: 'inherit',
                    textAlign: 'left',
                    margin: 0,
                    border: '1px solid white',
                    borderRadius: '8px',
                    padding: '5px',
                    backgroundColor: 'black',
                    cursor: 'text',
                    width: '300px',
                  }}
                >
                  {message}
                </pre>
              ) : (
                <Typography fontWeight="bold">
                  {t(
                    'management_menu.asset_wizard.sensor.test_connection_modal.waiting_message',
                  )}
                </Typography>
              )}
            </Stack>
          </Stack>
        )
      }
    />
  );
};

TestConnectionModal.propTypes = {
  modalShow: PropTypes.bool.isRequired,
  onSenderModalHide: PropTypes.func.isRequired,
  parameter: PropTypes.string,
  sensor: PropTypes.string,
  measuringMode: PropTypes.string,
  credentials: PropTypes.object,
};

export default TestConnectionModal;
